import { useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Calendar, Mail, Users, ArrowRight, Briefcase } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

export default function ApplicationThankYou() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const jobTitle = searchParams.get("job") || "this position";
  const applicantName = searchParams.get("name");

  useSEO({
    title: "Application Submitted | Ommni Careers",
    description: "Thank you for your application. Learn about the next steps in our hiring process.",
    keywords: "job application, hiring process, careers",
    canonical: "https://ommni.io/application-thank-you",
  });

  // Redirect if accessed directly without coming from application
  useEffect(() => {
    if (!searchParams.get("job")) {
      navigate("/careers");
    }
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <div className="flex-1 pt-32 pb-16 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Success Header */}
          <div className="text-center space-y-6 mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">
              Application Submitted Successfully!
            </h1>
            {applicantName && (
              <p className="text-xl text-muted-foreground">
                Thank you, {applicantName}!
              </p>
            )}
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We've received your application for <span className="font-semibold text-foreground">{jobTitle}</span> and 
              are excited to review your qualifications.
            </p>
          </div>

          {/* What Happens Next */}
          <div className="space-y-8 mb-12">
            <h2 className="text-3xl font-bold text-center">What Happens Next?</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold">Confirmation Email</h3>
                  </div>
                  <p className="text-muted-foreground">
                    You'll receive a confirmation email within the next few minutes with your 
                    application details and reference number.
                  </p>
                  <div className="text-sm font-medium text-primary">
                    Within 5 minutes
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold">Initial Review</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Our hiring team will carefully review your application and CV to assess 
                    your fit for the role.
                  </p>
                  <div className="text-sm font-medium text-primary">
                    Within 1-2 business days
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold">Interview Invitation</h3>
                  </div>
                  <p className="text-muted-foreground">
                    If your qualifications match our needs, we'll reach out to schedule 
                    an initial interview with the hiring manager.
                  </p>
                  <div className="text-sm font-medium text-primary">
                    Within 1-2 weeks
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                      <CheckCircle2 className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold">Final Decision</h3>
                  </div>
                  <p className="text-muted-foreground">
                    After the interview process, we'll make our decision and reach out with 
                    next steps, regardless of the outcome.
                  </p>
                  <div className="text-sm font-medium text-primary">
                    Within 2-4 weeks
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Important Information */}
          <Card className="mb-12 bg-muted/30 border-2">
            <CardContent className="p-8 space-y-4">
              <h3 className="text-2xl font-bold">Important Information</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Check your spam folder</strong> - Sometimes our emails end up there. 
                    Add people@ommni.io to your contacts to ensure delivery.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Response timeline</strong> - We aim to respond to all applicants within 2 weeks, 
                    even if we decide not to move forward with your application.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Questions?</strong> Feel free to reach out to people@ommni.io 
                    if you have any questions about your application or the process.
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* CTAs */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="text-center p-8 hover:border-primary/50 transition-colors">
              <CardContent className="space-y-4">
                <Briefcase className="w-12 h-12 text-primary mx-auto" />
                <h3 className="text-xl font-semibold">Explore More Positions</h3>
                <p className="text-muted-foreground">
                  Check out our other open positions that might interest you
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/careers">
                    View All Positions
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center p-8 hover:border-primary/50 transition-colors">
              <CardContent className="space-y-4">
                <Users className="w-12 h-12 text-primary mx-auto" />
                <h3 className="text-xl font-semibold">Learn About Ommni</h3>
                <p className="text-muted-foreground">
                  Discover our mission, values, and what it's like to work here
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/about">
                    About Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Contact Section */}
          <div className="mt-12 text-center p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-purple-500/10 border-2 border-primary/20">
            <h3 className="text-2xl font-bold mb-4">Need Help?</h3>
            <p className="text-muted-foreground mb-6">
              Our team is here to answer any questions you have about your application or the hiring process.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg">
                <a href="mailto:people@ommni.io">
                  <Mail className="mr-2 w-4 h-4" />
                  Email Us
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/contact">Contact Form</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
